import React, { useState, useCallback, useEffect } from 'react';
import { AutoComplete, Input, Button, Card, Typography, Space, Tag, Spin, Alert, message } from 'antd';
import { SearchOutlined, StockOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { debounce } from 'lodash-es';
import { analysisApi } from '../../services/api';
import './index.css';

const { Title, Text } = Typography;

// 股票信息接口
interface StockInfo {
  symbol: string;
  name: string;
  market: string;
  exchange?: string;
  industry?: string;
  market_cap?: number;
}

// 搜索选项接口
interface SearchOption {
  key: string;
  value: string;
  label: React.ReactNode;
  stock: StockInfo;
}

interface StockSearchProps {
  onStockSelect: (stock: StockInfo) => void;
  selectedStock?: StockInfo | null;
  placeholder?: string;
  disabled?: boolean;
}

const StockSearch: React.FC<StockSearchProps> = ({
  onStockSelect,
  selectedStock,
  placeholder = "输入股票代码或名称搜索...",
  disabled = false
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [recentStocks, setRecentStocks] = useState<StockInfo[]>([]);
  const [error, setError] = useState<string>('');
  const [searchAttempts, setSearchAttempts] = useState<number>(0);
  const [lastSearchQuery, setLastSearchQuery] = useState<string>('');

  // 从localStorage加载最近搜索的股票
  useEffect(() => {
    const saved = localStorage.getItem('recentStocks');
    if (saved) {
      try {
        setRecentStocks(JSON.parse(saved));
      } catch (error) {
        console.error('加载最近搜索失败:', error);
      }
    }
  }, []);

  // 保存最近搜索的股票
  const saveRecentStock = useCallback((stock: StockInfo) => {
    const updated = [stock, ...recentStocks.filter(s => s.symbol !== stock.symbol)].slice(0, 10);
    setRecentStocks(updated);
    localStorage.setItem('recentStocks', JSON.stringify(updated));
  }, [recentStocks]);

  // 搜索股票API调用
  const searchStocks = useCallback(async (query: string): Promise<StockInfo[]> => {
    if (!query.trim()) return [];

    try {
      const results = await analysisApi.searchStocks(query);
      setError(''); // 清除之前的错误
      setSearchAttempts(0); // 重置重试次数
      return results;
    } catch (error: any) {
      console.error('搜索股票失败:', error);
      
      // 分类错误处理
      let errorMessage = '搜索失败，请重试';
      if (error?.response?.status === 404) {
        errorMessage = '未找到相关股票，请检查股票代码或名称';
      } else if (error?.response?.status === 500) {
        errorMessage = '服务器内部错误，请稍后重试';
      } else if (error?.response?.status === 503) {
        errorMessage = '数据源暂时不可用，请稍后重试';
      } else if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network')) {
        errorMessage = '网络连接失败，请检查网络连接';
      } else if (error?.code === 'TIMEOUT') {
        errorMessage = '请求超时，请重试';
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
      setSearchAttempts(prev => prev + 1);
      
      // 显示用户友好的错误提示
      if (searchAttempts < 2) {
        message.warning(errorMessage);
      } else {
        message.error(`${errorMessage}（已重试${searchAttempts}次）`);
      }
      
      return [];
    }
  }, [searchAttempts]);

  // 重试搜索函数
  const retrySearch = useCallback(async () => {
    if (!lastSearchQuery.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const stocks = await searchStocks(lastSearchQuery);
      const searchOptions: SearchOption[] = stocks.map((stock, index) => ({
        key: `${stock.symbol}-${stock.market}-${index}`,
        value: `${stock.symbol} - ${stock.name}`,
        label: (
          <div className="stock-search-option">
            <div className="stock-info">
              <span className="stock-symbol">{stock.symbol}</span>
              <span className="stock-name">{stock.name}</span>
            </div>
            <div className="stock-meta">
              <Tag color="blue">{stock.market}</Tag>
              {stock.exchange && (
                <Tag color="green">{stock.exchange}</Tag>
              )}
              {stock.industry && (
                <Text type="secondary" className="stock-industry">
                  {stock.industry}
                </Text>
              )}
            </div>
          </div>
        ),
        stock
      }));
      setOptions(searchOptions);
      message.success('重试成功');
    } catch (error) {
      console.error('重试搜索失败:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [lastSearchQuery, searchStocks]);

  // 防抖搜索
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setOptions([]);
        setLoading(false);
        setError('');
        setLastSearchQuery('');
        return;
      }

      setLoading(true);
      setLastSearchQuery(query);
      
      try {
        let stocks = await searchStocks(query);
        // 回退逻辑：若用户仅输入6位数字代码，分别尝试 .SZ / .SH 后缀
        if ((!stocks || stocks.length === 0) && /^\d{6}$/.test(query)) {
          const szQuery = `${query}.SZ`;
          const shQuery = `${query}.SH`;
          stocks = await searchStocks(szQuery);
          if (!stocks || stocks.length === 0) {
            stocks = await searchStocks(shQuery);
          }
        }
        let searchOptions: SearchOption[] = stocks.map((stock, index) => ({
          key: `${stock.symbol}-${stock.market}-${index}`,
          value: `${stock.symbol} - ${stock.name}`,
          label: (
            <div className="stock-search-option">
              <div className="stock-info">
                <span className="stock-symbol">{stock.symbol}</span>
                <span className="stock-name">{stock.name}</span>
              </div>
              <div className="stock-meta">
                <Tag color="blue">{stock.market}</Tag>
                {stock.exchange && (
                  <Tag color="green">{stock.exchange}</Tag>
                )}
                {stock.industry && (
                  <Text type="secondary" className="stock-industry">
                    {stock.industry}
                  </Text>
                )}
              </div>
            </div>
          ),
          stock
        }));
        
        // 移除阻塞式的实时数据获取，提高搜索响应速度
        // 如果需要更新名称，可以在用户选择后处理
        
        setOptions(searchOptions);
      } catch (error) {
        console.error('搜索出错:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [searchStocks]
  );

  // 处理搜索输入变化
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  // 处理股票选择
  const handleSelect = useCallback(async (value: string, option: SearchOption) => {
    let stock = option.stock;
    // 异步补充实时名称（非阻塞）
    (async () => {
      try {
        if (stock && stock.name === stock.symbol) {
          const rt = await analysisApi.getRealtimeData(stock.symbol);
          if (rt && rt.name) {
            stock = { ...stock, name: rt.name } as StockInfo;
            saveRecentStock(stock);
            // 触发一次更新以刷新UI
            setSearchValue(`${stock.symbol} - ${stock.name}`);
          }
        }
      } catch (_) { void 0; }
    })();
    
    setSearchValue(`${stock.symbol} - ${stock.name}`);
    setOptions([]);
    saveRecentStock(stock);
    onStockSelect(stock);
  }, [onStockSelect, saveRecentStock]);

  // 处理最近股票点击
  const handleRecentStockClick = useCallback((stock: StockInfo) => {
    setSearchValue(`${stock.symbol} - ${stock.name}`);
    onStockSelect(stock);
  }, [onStockSelect]);

  // 清除搜索
  const handleClear = useCallback(() => {
    setSearchValue('');
    setOptions([]);
    onStockSelect(null as any);
  }, [onStockSelect]);

  // 格式化市值
  const formatMarketCap = (marketCap?: number) => {
    if (!marketCap) return '';
    if (marketCap >= 1e12) return `${(marketCap / 1e12).toFixed(1)}万亿`;
    if (marketCap >= 1e8) return `${(marketCap / 1e8).toFixed(1)}亿`;
    if (marketCap >= 1e4) return `${(marketCap / 1e4).toFixed(1)}万`;
    return marketCap.toString();
  };

  return (
    <div className="stock-search">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* 搜索输入框 */}
        <AutoComplete
          id="stock-search-autocomplete"
          value={searchValue}
          options={options}
          onSearch={handleSearch}
          onSelect={handleSelect}
          disabled={disabled}
          style={{ width: '100%' }}
          notFoundContent={loading ? <Spin size="small" /> : '暂无搜索结果'}
          allowClear
          onClear={handleClear}
        >
          <Input
            id="stock-search-input"
            placeholder={placeholder}
            prefix={<SearchOutlined />}
            suffix={loading && <Spin size="small" />}
            size="large"
          />
        </AutoComplete>

        {/* 错误提示 */}
        {error && (
          <Alert
            message="搜索出错"
            description={
              <div>
                <div>{error}</div>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<ReloadOutlined />}
                  style={{ padding: 0, marginTop: 8 }}
                  onClick={retrySearch}
                  loading={loading}
                  disabled={!lastSearchQuery.trim()}
                >
                  重试
                </Button>
              </div>
            }
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError('')}
          />
        )}

        {/* 当前选中的股票 */}
        {selectedStock && (
          <Card size="small" className="selected-stock">
            <div className="selected-stock-info">
              <div className="stock-header">
                <span className="stock-symbol">{selectedStock.symbol}</span>
                <span className="stock-name">{selectedStock.name}</span>
              </div>
              <div className="stock-details">
                <Space wrap>
                  <Tag color="blue">{selectedStock.market}</Tag>
                  {selectedStock.exchange && (
                    <Tag color="green">{selectedStock.exchange}</Tag>
                  )}
                  {selectedStock.industry && (
                    <Tag color="orange">{selectedStock.industry}</Tag>
                  )}
                  {selectedStock.market_cap && (
                    <Tag color="purple">
                      市值: {formatMarketCap(selectedStock.market_cap)}
                    </Tag>
                  )}
                </Space>
              </div>
            </div>
          </Card>
        )}

        {/* 最近搜索 */}
        {!selectedStock && recentStocks.length > 0 && (
          <div className="recent-stocks">
            <Text type="secondary" className="recent-title">最近搜索:</Text>
            <div className="recent-list">
              {recentStocks.slice(0, 5).map((stock) => (
                <Button
                  key={stock.symbol}
                  type="text"
                  size="small"
                  className="recent-stock-btn"
                  onClick={() => handleRecentStockClick(stock)}
                >
                  <span className="recent-symbol">{stock.symbol}</span>
                  <span className="recent-name">{stock.name}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </Space>
    </div>
  );
};

export default StockSearch;
